import { z } from 'zod';

// z.string().uuid() uses Zod v4's strict RFC 4122 pattern (version nibble [1-8])
// which rejects dev-seed sequential UUIDs (000...0001). Postgres enforces the
// uuid type at the DB level, so format validation here is redundant.
export const AccountIdSchema = z.string().brand<'AccountId'>();
export type AccountId = z.infer<typeof AccountIdSchema>;

export const UserIdSchema = z.string().brand<'UserId'>();
export type UserId = z.infer<typeof UserIdSchema>;

export const CardConditionSchema = z.enum([
  'Near Mint',
  'Lightly Played',
  'Moderately Played',
  'Heavily Played',
  'Damaged',
]);
export type CardCondition = z.infer<typeof CardConditionSchema>;

/**
 * The DB-facing `container_type` discriminator (`card_instances.container_type`,
 * resolved by the `container_directory` view). This is NOT the container
 * spine's presentation vocabulary — `ContainerSpineType` in
 * `apps/web/src/components/containers/types.ts` uses its own short-form keys
 * (`case`, `sealed`) that never matched this enum 1:1. See that file's
 * docblock for the mapping; the two vocabularies are kept deliberately
 * distinct, not collapsed.
 *
 * Widened 2026-08-04 (#441): `slab_binder` removed — Slab Binders are
 * dropped, not parked (decided 2026-08-01). `vault` added — the fifth
 * container type, reinstated 2026-08-03. Widening a Zod enum is safe;
 * narrowing one is not (the #101 all-or-nothing parse shape), so this
 * direction only.
 */
export const ContainerKindSchema = z.enum([
  'bin',
  'binder',
  'display_case',
  'sealed_stock',
  'vault',
]);
export type ContainerKind = z.infer<typeof ContainerKindSchema>;

/** Known TCG card finish treatments. Stored as plain text in the DB to allow
 *  future expansion without a migration; Zod guards the API boundary. */
export const CardFinishSchema = z.enum(['nonfoil', 'foil', 'etched']);
export type CardFinish = z.infer<typeof CardFinishSchema>;

export const CardSkuIdSchema = z.string().brand<'CardSkuId'>();
export type CardSkuId = z.infer<typeof CardSkuIdSchema>;

/** Attribute tuple that uniquely identifies a fungible SKU within a shop. */
export const CardSkuAttributesSchema = z.object({
  condition: CardConditionSchema,
  finish: CardFinishSchema,
  /** ISO 639-1 language code (e.g. 'en', 'ja', 'de'). */
  language: z.string().min(2).max(10),
  signed: z.boolean(),
  altered: z.boolean(),
});
export type CardSkuAttributes = z.infer<typeof CardSkuAttributesSchema>;

/**
 * Row shape returned by a `SELECT *` on `barcode_sequences`.
 *
 * One row per ACCOUNT, not per user: `20260720001000_per_instance_ownership.sql`
 * dropped `user_id` and made `account_id` the unique key, because two seats on
 * one account each holding a private counter would mint the same
 * `{prefix}{seq}` twice and collide on `card_instances_account_id_barcode_key`.
 * `user_id` is deliberately absent — this schema is the boundary that would have
 * caught the client still reading it.
 */
export const BarcodeSequenceSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  prefix: z
    .string()
    .max(6)
    .regex(/^[A-Za-z0-9]*$/),
  next_seq: z.number().int().min(1),
  created_at: z.string(),
  updated_at: z.string(),
});
export type BarcodeSequence = z.infer<typeof BarcodeSequenceSchema>;

/**
 * Row shape returned by a `SELECT id, monthly_price_cents` on `tiers`.
 *
 * `tiers` is a globally readable reference table (CLAUDE.md guardrail #1 /
 * `accountScopingGuard.test.ts` `SAFE_TABLES`) — no `account_id` column, no
 * account-scoped predicate. `monthly_price_cents` is the single source of
 * truth for the price quoted on `/subscribe` and the lapsed-account upsell
 * (vendor#156, which existed because four call sites each carried their own
 * literal instead of reading this column).
 */
export const TierPricingSchema = z.object({
  id: z.string(),
  monthly_price_cents: z.number().int().nonnegative(),
});
export type TierPricing = z.infer<typeof TierPricingSchema>;

/**
 * The account's flat sticker-spread threshold (`public.spread_settings`,
 * `supabase/migrations/20260902120000_spread_config_and_card_notes.sql`,
 * vendor#180/#381). One row per account, keyed on `account_id` (the PK) — a
 * second row is unrepresentable rather than merely unexpected.
 *
 * NO row means the account has never configured spread and the drift engine
 * falls back to the historical hardcoded `percent` / `15`
 * (`supabase/functions/compute_analytics/spread-config.ts` `FALLBACK_TYPE` /
 * `FALLBACK_VALUE`) — the absence is meaningful and must not be defaulted
 * away before the caller can tell "never configured" from "configured to
 * 15%" (vendor#389).
 *
 * UNITS: `default_value` is WHOLE DOLLARS when `default_type` is `'amount'`
 * (the engine compares `absCents > default_value * 100`), a PERCENTAGE when
 * `'percent'`. Never cents — `spread_bands.min_cents` is the only cents
 * column in this feature, and the band ladder itself is vendor#391, not
 * built by the schema this type describes.
 */
export const SpreadThresholdTypeSchema = z.enum(['amount', 'percent']);
export type SpreadThresholdType = z.infer<typeof SpreadThresholdTypeSchema>;

export const SpreadSettingsSchema = z.object({
  account_id: z.string(),
  default_type: SpreadThresholdTypeSchema,
  default_value: z.number().nonnegative(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SpreadSettings = z.infer<typeof SpreadSettingsSchema>;

/**
 * A row of `public.spread_bands` (`supabase/migrations/20260902120000_
 * spread_config_and_card_notes.sql`, vendor#391) — one split point in the
 * account's price-band ladder. STORAGE IS A SPLIT POINT, NOT A RANGE: there
 * is deliberately no `max_cents` field. `min_cents` is where this band
 * STARTS (inclusive); where it ends is wherever the row with the next
 * greater `min_cents` starts, or infinity for the row with the greatest
 * `min_cents` in the account's ladder. A non-empty ladder's lowest row is
 * always 0 (`_enforce_spread_band_ladder`, enforced server-side) — see that
 * function's comment for why an ungrounded floor would silently drop every
 * cheaper card from the spread report.
 *
 * UNITS: `min_cents` is CENTS. `value` is the same unit convention as
 * {@link SpreadSettings.default_value} — WHOLE DOLLARS when `type` is
 * `'amount'`, a PERCENTAGE when `'percent'`. Never render `min_cents`
 * un-divided, and never label a `value` in cents.
 */
export const SpreadBandSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  min_cents: z.number().int().nonnegative(),
  type: SpreadThresholdTypeSchema,
  value: z.number().nonnegative(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SpreadBand = z.infer<typeof SpreadBandSchema>;

export * from './vision';
